import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import { flushSync } from "react-dom";
import { Download, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PrintableNotes } from "./PrintableNotes";
import { RevisionSheet } from "./RevisionSheet";

const VARIANTS = {
  full: { Doc: PrintableNotes, suffix: "Notes", label: "Download PDF", Icon: Download, wait: "Preparing your PDF — this can take up to a minute" },
  sheet: { Doc: RevisionSheet, suffix: "Revision-Sheet", label: "Revision sheet", Icon: FileText, wait: "Preparing your revision sheet…" },
};

const PAGE_W = 210, PAGE_H = 297, MARGIN = 10, GAP = 2.5;

// Renders each .pdf-item as an image and flows them onto A4 pages without splitting any item.
async function buildPdf(notes, v) {
  const [{ toCanvas, getFontEmbedCSS }, { jsPDF }] = await Promise.all([import("html-to-image"), import("jspdf")]);
  const host = document.createElement("div");
  host.style.cssText = "position:fixed;left:-10000px;top:0;";
  document.body.appendChild(host);
  const root = createRoot(host);
  flushSync(() => root.render(<v.Doc notes={notes} />));
  try {
    await document.fonts.ready;
    const fontEmbedCSS = await getFontEmbedCSS(host);
    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const usableW = PAGE_W - 2 * MARGIN, usableH = PAGE_H - 2 * MARGIN;
    let y = MARGIN;
    for (const el of host.querySelectorAll(".pdf-item")) {
      if (el.dataset.break === "true" && y > PAGE_H - 80) { pdf.addPage(); y = MARGIN; }
      const canvas = await toCanvas(el, { pixelRatio: 1.7, backgroundColor: "#ffffff", fontEmbedCSS });
      let w = usableW, h = (canvas.height * usableW) / canvas.width;
      if (h > usableH) { w = (w * usableH) / h; h = usableH; }
      if (y + h > PAGE_H - MARGIN) { pdf.addPage(); y = MARGIN; }
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", MARGIN + (usableW - w) / 2, y, w, h);
      y += h + (el.dataset.break === "true" ? 1 : GAP);
    }
    pdf.save(`${notes.chapter_title.replace(/[^a-z0-9]+/gi, "-")}-Class-${notes.class_level}-${v.suffix}.pdf`);
  } finally {
    root.unmount();
    host.remove();
  }
}

export const DownloadNotesPdf = ({ notes, variant = "full" }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const v = VARIANTS[variant];

  const onClick = async () => {
    if (!user) return toast.error("Log in to download the notes as a PDF");
    setBusy(true);
    toast(v.wait);
    try {
      await buildPdf(notes, v);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Couldn't create the PDF, please try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button onClick={onClick} disabled={busy}
      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs font-semibold text-zinc-200 hover:border-yellow-300/60 hover:text-yellow-200 transition-colors disabled:opacity-60"
      data-testid={variant === "full" ? "notes-download-pdf" : "notes-download-revision-sheet"}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <v.Icon className="w-3.5 h-3.5" />}
      {busy ? "Preparing PDF…" : v.label}
    </button>
  );
};
